import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Button } from '../../../../../components/Button'
import { SearchInput } from '../../../../../components/SearchInput'
import { type Column } from '../../../../../components/Table'
import { Medias } from '../../../../../enumns/medias'
import { useMediaQuery } from '../../../../../hooks/useMediaQuery'
import { useClientCreateMutation } from '../../../../../services/hooks/clients/mutations/useClientCreateMutation'
import { useClientEditMutation } from '../../../../../services/hooks/clients/mutations/useClientEditMutation'
import { useClientRemoveMutation } from '../../../../../services/hooks/clients/mutations/useClientRemoveMutation'
import { type ClientDomain } from '../../../../../types/domain/client-domain'
import { type ClientPersistence } from '../../../../../types/persistence/client-persistence'
import { TableRowActions } from '../../components/TableRowActions'
import { TableWithInfiniteScroll } from './components/TableWithInfiniteScroll'
import { TableWithManuallyPagination } from './components/TableWithManuallyPagination'
import { NavigationSelect } from '../../components/NavigationSelect'
import {
  TOAST_ERROR_DEFAULT_OPTIONS,
  TOAST_SUCCESS_DEFAULT_OPTIONS,
} from '../../../../../constants/toast'
import { type FiltersDomain } from '../../../../../types/domain/filters-domain'
import { getErrorMessage } from '../../../../../utils/getErrorMessage'
import { ClientContactsFormsManager } from './components/ClientContactsFormsManager'

export const Client = () => {
  const navigate = useNavigate()
  const isMinScreenWidthMD = useMediaQuery(`(min-width: ${Medias.MD}px)`)
  const isMinScreenWidthLG = useMediaQuery(`(min-width: ${Medias.LG}px)`)

  const queryClient = useQueryClient()
  const clientCreationMutation = useClientCreateMutation()
  const clientRemotionMutation = useClientRemoveMutation()
  const clientEditionMutation = useClientEditMutation()

  const [clientToEdit, setClientToEdit] = useState()
  const [isFormModalOpen, setIsFormModalOpen] = useState(false)
  const [filters, setFilters] = useState<FiltersDomain>({
    page: 1,
    take: isMinScreenWidthMD ? 10 : 20,
    search: '',
    order: 'DESC',
    sort: 'createdAt',
  })

  const isFormInEditMode = Boolean(clientToEdit)

  const getCachedClientById = (id: number) => {
    const clientsFromCache: any = queryClient.getQueryData(['clients', filters])

    if (isMinScreenWidthMD) {
      return clientsFromCache.data.find(
        (client: ClientDomain) => client.id === id
      )
    }

    return clientsFromCache?.pages
      ?.reduce((acc: any, item: { data: ClientDomain[] }) => {
        return [...acc, ...item.data]
      }, [])
      .find((client: ClientDomain) => client.id === id)
  }

  const columns = [
    {
      field: 'id',
      isVisible: false,
    },
    {
      field: 'name',
      headerName: 'Nome',
      widthInPercentage: 90,
    },
    {
      field: 'actions',
      headerName: 'Ações',
      widthInPercentage: 10,
      renderCell ({ id }: { id: number }) {
        return (
          <TableRowActions
            tableContentType="Client"
            isRemovingRegister={clientRemotionMutation.isLoading}
            alertMessage="Você está prestes a excluir um cliente. Deseja continuar?"
            onEdit={() => {
              setClientToEdit(getCachedClientById(id))
              setIsFormModalOpen(true)
            }}
            onRemove={(successAction) => handleRemoveClient(id, successAction)}
            onView={() => navigate(`${id}/contacts`)}
          />
        )
      },
    },
  ] as Column[]

  const createClient = (data: ClientPersistence) => {
    clientCreationMutation.mutate(data, {
      onSuccess: () => {
        toast.success(
          'Cliente cadastrado com sucesso',
          TOAST_SUCCESS_DEFAULT_OPTIONS
        )

        setIsFormModalOpen(false)
        queryClient.invalidateQueries(['clients', filters])
      },
      onError (error) {
        toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
      },
    })
  }

  const editClient = (id: number, data: ClientPersistence) => {
    clientEditionMutation.mutate(
      { id, data },
      {
        onSuccess: () => {
          toast.success(
            'Cliente editado com sucesso',
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )

          setClientToEdit(undefined)
          setIsFormModalOpen(false)
          queryClient.invalidateQueries(['clients', filters])
        },
        onError (error) {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      }
    )
  }

  const handleSaveClient = (data: ClientPersistence) => {
    if (
      data.contacts &&
      Object.values(data.contacts[0]).filter(Boolean).length === 0
    ) {
      delete data.contacts
    }

    if (isFormInEditMode) {
      editClient((clientToEdit as any).id, data)
      return
    }

    createClient(data)
  }

  const handleRemoveClient = useCallback(
    (id: number, successAction: () => void) => {
      clientRemotionMutation.mutate(id, {
        onSuccess: () => {
          toast.success(
            'Cliente excluído com sucesso',
            TOAST_SUCCESS_DEFAULT_OPTIONS
          )

          setIsFormModalOpen(false)
          queryClient.invalidateQueries(['clients', filters])
          successAction()
        },
        onError: (error) => {
          toast.error(getErrorMessage(error), TOAST_ERROR_DEFAULT_OPTIONS)
        },
      })
    },
    [clientRemotionMutation]
  )

  const handleSearchClient = (debouncedSearchTerm: string) =>
    setFilters((prevState) => ({
      ...prevState,
      page: 1,
      search: debouncedSearchTerm,
    }))

  const handleCreateButtonClick = () => {
    if (isFormInEditMode) {
      setClientToEdit(undefined)
    }

    setIsFormModalOpen(true)
  }

  useEffect(() => {
    if (isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        page: 1,
      }))
      return
    }

    if (!isMinScreenWidthMD) {
      setFilters((prevState) => ({
        ...prevState,
        take: 20,
        page: 1,
      }))
    }
  }, [isMinScreenWidthMD])

  return (
    <>
      <div>
        {!isMinScreenWidthLG && <NavigationSelect defaultRoute="client" />}

        <div className="md:flex md:justify-between md:items-center md:mb-6">
          <h1 className="mb-4 font-medium text-grey-900 md:text-lg md:mb-0">
            Cliente
          </h1>

          <div className="mb-4 md:flex md:gap-2">
            <SearchInput
              onSearch={handleSearchClient}
              placeholder="Buscar por nome"
              className="mb-6 md:w-96 md:mb-0"
            />

            <Button className="h-10 ml-auto" onClick={handleCreateButtonClick}>
              Cadastrar
            </Button>
          </div>
        </div>

        {isMinScreenWidthMD ? (
          <TableWithManuallyPagination
            columns={columns}
            filters={filters}
            setFilters={setFilters}
          />
        ) : (
          <TableWithInfiniteScroll columns={columns} filters={filters} />
        )}
      </div>

      {isFormModalOpen && (
        <ClientContactsFormsManager
          defaultValues={clientToEdit}
          onClose={() => setIsFormModalOpen(false)}
          onSubmit={handleSaveClient}
          isSubmitting={
            clientCreationMutation.isLoading || clientEditionMutation.isLoading
          }
        />
      )}
    </>
  )
}
